class EartheraHeader extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.section-header');
    this.update = () => this.classList.toggle('is-scrolled', window.scrollY > 10);
    this.onClick = (event) => {
      this.classList.toggle('is-interacting', this.section.contains(event.target));
    };
    this.onKeydown = (event) => {
      if (event.key === 'Escape') this.classList.remove('is-interacting');
    };
    this.measure = () => {
      document.documentElement.style.setProperty('--header-height', `${this.section.offsetHeight}px`);
    };
    // :has() cannot be nested inside :has(), so the open state of the mega menu / mobile drawer
    // is exposed as a class that the CSS (including .section-header rules) can use.
    this.syncMenuOpen = () => {
      const isOpen = Boolean(this.querySelector('details.mega-menu[open], #Details-menu-drawer-container[open]'));
      if (isOpen !== this.classList.contains('is-menu-open')) this.classList.toggle('is-menu-open', isOpen);
    };
    this.update();
    this.measure();
    this.addEventListener('toggle', this.syncMenuOpen, true);
    this.syncMenuOpen();
    this.observer = new ResizeObserver(this.measure);
    this.observer.observe(this.section);
    window.addEventListener('scroll', this.update, { passive: true });
    document.addEventListener('click', this.onClick);
    document.addEventListener('keydown', this.onKeydown);
    this.setupMegaMenuHover();
  }

  setupMegaMenuHover() {
    const hoverMedia = window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');
    this.megaMenuListeners = Array.from(this.querySelectorAll('.header__inline-menu details.mega-menu'), (details) => {
      const summary = details.querySelector('summary');
      let closeTimer;
      const cancelClose = () => window.clearTimeout(closeTimer);
      const onEnter = (event) => {
        if (!hoverMedia.matches || event.pointerType === 'touch') return;
        cancelClose();
        this.megaMenuListeners.forEach((menu) => {
          if (menu.details !== details) menu.close();
        });
        details.open = true;
        summary.setAttribute('aria-expanded', 'true');
      };
      const close = () => {
        cancelClose();
        details.open = false;
        summary.setAttribute('aria-expanded', 'false');
      };
      const onLeave = (event) => {
        if (!hoverMedia.matches || event.pointerType === 'touch') return;
        cancelClose();
        // Allow the pointer to cross the gap between the trigger and panel.
        closeTimer = window.setTimeout(() => {
          if (!details.contains(document.activeElement)) close();
        }, 250);
      };
      const onToggle = () => summary.setAttribute('aria-expanded', String(details.open));
      // The parent item links to its page (e.g. BESTSELLER -> collection).
      // Mouse: hover opens the mega menu, a click anywhere on the trigger (incl. its padding) navigates.
      // Touch / no hover: first tap opens the menu, a second tap on the label follows the link.
      // Keyboard (click with detail 0): Enter on the summary still toggles the menu.
      const labelLink = summary.querySelector('a.header-menu-label--link');
      const onSummaryClick = (event) => {
        if (!labelLink || event.detail === 0) return;
        const isMouse = hoverMedia.matches && event.pointerType !== 'touch';
        if (isMouse) {
          if (event.target.closest('a') === labelLink) return; // native link navigation
          event.preventDefault();
          labelLink.click();
          return;
        }
        if (details.open && event.target.closest('a') === labelLink) return;
        event.preventDefault();
        this.megaMenuListeners.forEach((menu) => {
          if (menu.details !== details) menu.close();
        });
        details.open = !details.open;
      };
      summary.addEventListener('click', onSummaryClick);
      details.addEventListener('pointerenter', onEnter);
      details.addEventListener('pointerleave', onLeave);
      details.addEventListener('toggle', onToggle);
      return {
        details,
        close,
        cleanup: () => {
          cancelClose();
          details.removeEventListener('pointerenter', onEnter);
          details.removeEventListener('pointerleave', onLeave);
          details.removeEventListener('toggle', onToggle);
          summary.removeEventListener('click', onSummaryClick);
        },
      };
    });
  }

  disconnectedCallback() {
    this.megaMenuListeners.forEach((menu) => menu.cleanup());
    this.observer.disconnect();
    this.removeEventListener('toggle', this.syncMenuOpen, true);
    window.removeEventListener('scroll', this.update);
    document.removeEventListener('click', this.onClick);
    document.removeEventListener('keydown', this.onKeydown);
  }
}
customElements.define('earthera-header', EartheraHeader);
